import type { CoachingCanonicalCandidatesResponse, RecordCanonicalFollowupRequest } from '@/lib/types';
import { feedbackMessageExcerpt } from '@/features/feedback-authoring/feedbackContent';
import { followupSchema, type FollowupValues } from './followupForm';

export type Candidate = CoachingCanonicalCandidatesResponse['items'][number];
export type Anchor = Extract<Candidate, { kind: 'coaching_action_approved' | 'focus' }>;
export type Observation = Extract<Candidate, { event_domain: 'support' }>;
export type Support = Observation['support'];

export function isAnchor(item: Candidate, playerId: string): item is Anchor {
  return (item.kind === 'focus' || item.kind === 'coaching_action_approved' && item.candidate)
    && item.source.availability === 'available' && item.source.current && item.source.followup_candidate
    && item.source.reference.player_id === playerId;
}
export function isObservation(item: Candidate, playerId: string): item is Observation {
  return item.kind !== 'focus' && item.event_domain === 'support' && item.candidate
    && item.support.current && item.support.observed_candidate && item.support.current_context_eligible
    && item.support.basis === 'observed' && item.support.player_ids.includes(playerId)
    && item.support.event?.id === item.event_id && item.support.event_id === item.event_id;
}
export function anchorIdentity(item: Anchor): string {
  return JSON.stringify(['canonical', item.kind, item.kind === 'focus' ? [item.focus_id, item.revision] : item.event_id,
    item.source.reference]);
}
export function observationIdentity(item: Observation): string {
  return JSON.stringify(['canonical', item.event_domain, item.event_id, item.support.support_id,
    item.support.revision_id, item.support.version]);
}
export function supportText(support: Support): string {
  return 'statement' in support.payload ? support.payload.statement
    : [support.payload.body, support.payload.what_happened].filter(Boolean).join('\n');
}
export function anchorTitle(item: Anchor): string {
  if (item.source.availability !== 'available') return 'Source unavailable';
  const content = item.source.content;
  return 'message_excerpt' in content ? content.message_excerpt
    : 'message' in content ? feedbackMessageExcerpt(content.message) : content.title;
}
export function canonicalFollowupBody(values: FollowupValues, anchors: Anchor[], observations: Observation[],
  playerId: string, operationId: string): RecordCanonicalFollowupRequest {
  followupSchema.parse(values);
  const anchor = anchors.find(item => isAnchor(item, playerId) && anchorIdentity(item) === values.anchor);
  if (!anchor) throw new Error('Refresh and choose a current action or focus.');
  const observation = observations.find(item => isObservation(item, playerId) && observationIdentity(item) === values.support);
  if (values.outcome === 'observed' && !observation) throw new Error('Refresh and choose a current supporting observation.');
  return {
    schema_version: 'canonical-followup-record.v1', client_operation_id: operationId,
    reference: anchor.source.reference,
    anchor: anchor.kind === 'focus' ? { kind: 'focus', focus_id: anchor.focus_id, revision: anchor.revision }
      : { kind: 'action', event_id: anchor.event_id },
    outcome: values.outcome, occurrence_precision: values.precision,
    occurred_at: values.precision === 'unknown' ? null : values.occurredAt,
    support: values.outcome === 'observed' && observation ? {
      support_id: observation.support.support_id, revision_id: observation.support.revision_id,
      version: observation.support.version, event_id: observation.event_id,
    } : null,
  };
}
